import { format, formatDistanceToNow, isToday, isTomorrow, isPast } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Formatea un monto en pesos MXN: 1234.5 → "$1,234.50". null/undefined → "—".
 */
export function fmtMoney(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * Formatea una fecha como "may 11" o "may 11, 2027" si es de otro año.
 */
export function fmtShortDate(d: string | Date | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  const thisYear = new Date().getFullYear();
  if (date.getFullYear() === thisYear) {
    return format(date, 'MMM d', { locale: es });
  }
  return format(date, "MMM d, yyyy", { locale: es });
}

/**
 * Estado de vencimiento:
 *   - vencida (past)
 *   - hoy
 *   - mañana
 *   - futuro
 */
export type DueStatus = 'vencida' | 'hoy' | 'manana' | 'futuro' | 'sin_fecha';

export function dueStatus(d: string | Date | null | undefined): DueStatus {
  if (!d) return 'sin_fecha';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return 'sin_fecha';
  if (isToday(date)) return 'hoy';
  if (isTomorrow(date)) return 'manana';
  if (isPast(date)) return 'vencida';
  return 'futuro';
}

export function fmtRelative(d: string | Date | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return formatDistanceToNow(date, { addSuffix: true, locale: es });
}

/**
 * Fecha + hora en hora Monterrey (America/Monterrey, UTC−6). Explícita para no
 * depender de la zona del navegador. Ej: "17 jul, 14:35".
 */
export function fmtDateTimeMty(d: string | Date | number | null | undefined): string {
  if (d == null) return '—';
  const date = typeof d === 'string' || typeof d === 'number' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('es-MX', {
    timeZone: 'America/Monterrey',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/** epoch en segundos → hora Monterrey. null/undefined → "—". */
export function fmtEpochMty(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  return fmtDateTimeMty(seconds * 1000);
}

/** Tamaño de archivo legible: 1536 → "1.5 KB". */
export function fmtBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
